import { showNavigationDrawer } from '@/actions';
import { Card } from '@/components/elements/cards';
import { Button } from '@/components/elements/forms/buttons';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { InitialsAvatar } from '@/components/elements/redesign/Avatar';
import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';
import LogoutModal from '@/components/modules/modals/LogoutModal';
import DeleteAccount from '@/components/modules/UpdateUserInfo/DeleteAccount';
import UpdateUserInfo from '@/components/modules/UpdateUserInfo/UpdateUserInfo';
import useUpdateUserInfo, {
  UseUpdateUserInfoCallbackProps,
} from '@/components/modules/UpdateUserInfo/UpdateUserInfo.hooks';
import EmptyState from '@/components/templates/EmptyState';
import { SCREEN_NAME } from '@/constants/analytics';
import { useAccountNavigationContext } from '@/features/account-navigation/AccountNavigation.hooks';
import { useAppSelector } from '@/hooks';
import { LoginContext } from '@/hooks/useLogin';
import useMobileView from '@/hooks/useMobileView';
import useTrackScreenShown from '@/hooks/useTrackScreenShown';
import { _s, _t } from '@/locale';
import { handleLogoutClick } from '@/services/navigationServices';
import { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

const baseTranslationKey = `pages.my-profile`;

const LoggedOutView = () => {
  const loginContext = useContext(LoginContext);

  return (
    <EmptyState
      title={_s(`${baseTranslationKey}.emptyState.title`)}
      body={_s(`${baseTranslationKey}.emptyState.body`)}
      cta={loginContext.renderLoginButton({
        label: _t(`${baseTranslationKey}.emptyState.cta`),
        size: 'sm',
        initiatedFrom: 'direct_links',
        trackPromptEvent: true,
      })}
      icon={<img src="/images/illustrations/profile2.png" alt="" />}
      className="max-w-[40ch]"
    />
  );
};

function displayToast({ success, retry }: UseUpdateUserInfoCallbackProps) {
  toast(
    ({ closeToast }) => (
      <Snackbar
        label={_s(`${baseTranslationKey}.snackbar.label.${success ? 'success' : 'error'}`)}
        type={success ? 'success' : 'danger'}
        action={
          <button
            onClick={
              success
                ? closeToast
                : () => {
                    closeToast();
                    retry();
                  }
            }>
            {_s(`${baseTranslationKey}.snackbar.action.${success ? 'success' : 'error'}`)}
          </button>
        }
      />
    ),
    { autoClose: 3000 },
  );
}

const LoggedInView = () => {
  const dispatch = useDispatch();
  const { isMobileView } = useMobileView();
  const updateUserInfoProps = useUpdateUserInfo({ callback: displayToast });
  const { user } = useAppSelector((state) => state.users);
  const handleLogout = () => handleLogoutClick(dispatch);

  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const renderContent = () => {
    if (isMobileView) {
      return (
        <div className="pb-xxl">
          <Card className="mx-sm bg-white px-2">
            <UpdateUserInfo
              {...updateUserInfoProps}
              givenName
              familyName
              gender
              dob
              mobile
              updateAccountProtection={true}
              floatStyle={true}
            />
          </Card>
          <Card className="mx-sm bg-white px-2">
            <DeleteAccount />
          </Card>
        </div>
      );
    }

    const userInitials = getUserInitials(user?.about?.givenName || '', user?.about?.familyName || '');
    const name = `${user.about?.givenName ?? ''} ${user.about?.familyName ?? ''}`;

    return (
      <div className="py-4xl gap-xl lg:gap-4xl container flex w-full grid-cols-[1fr_2fr] flex-col lg:grid lg:items-start">
        <div className="flex-shrink-0 overflow-hidden">
          <Card className="!my-0 bg-white">
            <div className="gap-md flex flex-col">
              <InitialsAvatar initials={userInitials} size="xl" variant="default" />
              <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                <div className="text-black-900 overflow-hidden text-ellipsis text-xl font-bold">{name}</div>
                <div className="text-l text-black-600 overflow-hidden text-ellipsis">{user.contact?.email}</div>
                <div>
                  <Button
                    variant="link"
                    className="text-l pb-0 pl-0 pt-2"
                    title={_s('logOut')}
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      setShowLogoutModal(true);
                    }}>
                    {_s('logOut')}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className="flex-1">
          <Card className="!m-0 bg-white">
            <UpdateUserInfo
              {...updateUserInfoProps}
              givenName
              familyName
              gender
              dob
              mobile
              updateAccountProtection={true}
              floatStyle={true}
            />
          </Card>
          <Card className="bg-white">
            <DeleteAccount />
          </Card>
        </div>
      </div>
    );
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <div>
      <LogoutModal isOpen={showLogoutModal} onClick={handleLogout} onClose={handleCancelLogout} />
      {renderContent()}
    </div>
  );
};
const views = {
  loggedOut: LoggedOutView,
  loggedIn: LoggedInView,
};

const MyProfile = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { isMobileView } = useMobileView();
  const { user } = useAppSelector((state) => state.users);
  const accountNavigationContext = useAccountNavigationContext();

  useTrackScreenShown({ screen_name: SCREEN_NAME.MY_PROFILE });

  const CurrentView = views[user ? 'loggedIn' : 'loggedOut'];

  const handleOnBackButtonClick = () => {
    if (location.state?.fromAccountNavigation) {
      accountNavigationContext.onOpen();
    }

    if (isMobileView) {
      dispatch(showNavigationDrawer({ show: true }));
    }
    history.goBack();
  };

  return (
    <PageViewLayout
      type="subView"
      back
      onBackButtonClick={handleOnBackButtonClick}
      title={_s(`${baseTranslationKey}.title`)}>
      <CurrentView />
    </PageViewLayout>
  );
};

const getUserInitials = (firstname: string, lastname: string) => {
  return firstname && lastname ? firstname.charAt(0) + lastname.charAt(0) : '';
};

export default MyProfile;
